import React, { useState, useEffect } from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Box,
  useMediaQuery,
  Stack,
  Chip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import EmailsInput from "./EmailInputs";

const FILTER_OPTIONS = {
  store: {
    all: "All",
    customers: "Customers",
    custom: "Custom",
  },
  CRM: {
    Customer: "Customers",
    Contact: "Contacts",
    Lead: "Lead",
    Prospect: "Prospect",
  },
  NEWSLETTER: {
    all: "All Subscribers",
  },
};

const SUBFILTER_OPTIONS = {
  new: "New (<30 days)",
  old: "Old (>30 days)",
  joinDays: "By Joined Days",
};

const GROWTH_STAGE_OPTIONS = [
  "Startup",
  "Growing",
  "Established",
  "Enterprise",
];

const BUSINESS_SIZE_OPTIONS = ["Small", "Medium", "Large"];

const ToSelector = ({
  forValue,
  setForValue,
  emails,
  setEmails,
  customerJoinDays,
  setCustomerJoinDays,
  permissions = { canEdit: true },
  isEditMode = false,
  eventData,
  setEventData,
  selectedSubfilters = "",
  setSelectedSubfilters,
  isMarketingSubscribed = true,
}) => {
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down("sm"));

  const [localContext, setLocalContext] = useState(
    eventData?.context || "store"
  );
  const [localForValue, setLocalForValue] = useState(forValue);

  // Initialize activeFilter from prop selectedSubfilters
  const [activeFilter, setActiveFilter] = useState(selectedSubfilters || "");
  const [showAdvanced, setShowAdvanced] = useState(false);

  const BASE_FILTERS = FILTER_OPTIONS[localContext];

  useEffect(() => {
    if (eventData) {
      setLocalContext(eventData.context || "store");
      setLocalForValue(
        eventData.for || (eventData.context === "store" ? "all" : "Customer")
      );
      setCustomerJoinDays(eventData.customerJoinDays || "");
      setEmails(
        eventData.customEmails?.join(",") ||
          (Array.isArray(eventData.emails)
            ? eventData.emails.join(",")
            : eventData.emails || "")
      );
    }
  }, [eventData]);

  // Sync localForValue up whenever it changes
  useEffect(() => {
    setForValue(localForValue);
    setEventData((prev) => ({
      ...prev,
      context: localContext,
      for: localForValue,
      customEmails:
        localForValue === "custom"
          ? emails.split(",").filter((e) => e.trim())
          : [],
    }));
  }, [localContext, localForValue, emails]);

  // Sync activeFilter with outside prop if changed
  useEffect(() => {
    setActiveFilter(selectedSubfilters || "");
  }, [selectedSubfilters]);

  const handleContextChange = (event) => {
    const value = event.target.value;
    setLocalContext(value);
    setLocalForValue(value === "store" || value === "NEWSLETTER" ? "all" : "Customer");
    setActiveFilter("");
  };

  const handleBaseChange = (event) => {
    const value = event.target.value;
    setLocalForValue(value);
    setActiveFilter("");
  };

  const handleFilterChange = (filter) => {
    const newFilter = activeFilter === filter ? "" : filter;
    setActiveFilter(newFilter);
    setSelectedSubfilters(newFilter);

    if (newFilter !== "joinDays") {
      setCustomerJoinDays("");
    }
  };

  const handleAdvancedToggle = () => {
    setShowAdvanced(!showAdvanced);
  };

  const clearFilters = () => {
    setLocalContext("store");
    setLocalForValue("all");
    setActiveFilter("");
    setCustomerJoinDays("");
    setEmails("");
    setSelectedSubfilters("");
  };

  return (
    <Box
      sx={{
        p: isSm ? 1 : 2,
        background: "#fafbfc",
        borderRadius: 2,
        border: "1px solid #eee",
      }}
    >
      <Stack spacing={2}>
        <Stack
          direction={isSm ? "column" : "row"}
          spacing={isSm ? 2 : 2.5}
          alignItems={isSm ? "stretch" : "center"}
          flexWrap="wrap"
        >
          <FormControl sx={{ minWidth: 120, flexGrow: 1 }}>
            <InputLabel>Context</InputLabel>
            <Select
              value={localContext}
              label="Context"
              onChange={handleContextChange}
              size="small"
            >
              <MenuItem value="store">Store</MenuItem>
              <MenuItem value="CRM">CRM</MenuItem>
              {isMarketingSubscribed && <MenuItem value="NEWSLETTER">Newsletter</MenuItem>}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 120, flexGrow: 1 }}>
            <InputLabel>To</InputLabel>
            <Select
              value={localForValue}
              label="Recipients"
              onChange={handleBaseChange}
              size="small"
            >
              {Object.entries(BASE_FILTERS).map(([key, label]) => (
                <MenuItem key={key} value={key}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {localForValue === "custom" && (
            <Box sx={{ flexGrow: 1 }}>
              <EmailsInput
                emails={emails.split(",").filter((e) => e.trim())}
                setEmails={(newEmails) => setEmails(newEmails.join(","))}
                disabled={isEditMode && !permissions.canEdit}
              />
            </Box>
          )}
        </Stack>

        {localForValue !== "all" && localForValue !== "custom" && (
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {Object.entries(SUBFILTER_OPTIONS).map(([key, label]) => (
              <Chip
                key={key}
                label={label}
                onClick={() => handleFilterChange(key)}
                color={activeFilter === key ? "primary" : "default"}
                variant={activeFilter === key ? "filled" : "outlined"}
                sx={{ m: 0.5 }}
              />
            ))}
          </Stack>
        )}

        {activeFilter === "joinDays" && (
          <TextField
            label="Joined Days"
            value={customerJoinDays}
            onChange={(e) =>
              setCustomerJoinDays(e.target.value.replace(/\D/g, ""))
            }
            sx={{ width: isSm ? "100%" : 200 }}
          />
        )}

        {localContext === "CRM" && (
          <>
            <Button
              onClick={handleAdvancedToggle}
              size="small"
              sx={{ alignSelf: "flex-start" }}
            >
            </Button>
              <Stack direction={isSm ? "column" : "row"} spacing={2}>
                <FormControl sx={{ minWidth: 120 }}>
                  <InputLabel>Growth Stage</InputLabel>
                  <Select
                    value={eventData?.growthStage || ""}
                    label="Growth Stage"
                    onChange={(e) =>
                      setEventData((prev) => ({
                        ...prev,
                        growthStage: e.target.value,
                      }))
                    }
                    size="small"
                  >
                    {GROWTH_STAGE_OPTIONS.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl sx={{ minWidth: 120 }}>
                  <InputLabel>Business Size</InputLabel>
                  <Select
                    value={eventData?.businessSize || ""}
                    label="Business Size"
                    onChange={(e) =>
                      setEventData((prev) => ({
                        ...prev,
                        businessSize: e.target.value,
                      }))
                    }
                    size="small"
                  >
                    {BUSINESS_SIZE_OPTIONS.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
          </>
        )}

        <Box>
          <Button
            variant="outlined"
            color="secondary"
            onClick={clearFilters}
            size="small"
          >
            Clear All
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};

export default ToSelector;
